import {
  useViewUsersSuspense,
  type ViewUsersRequest,
  type ViewUsersResponse
} from "@swivu/client-api";
import { useContext } from "react";
import { createContext } from "react";
import { DateTime } from "luxon";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import ErrorBoundary from "./ErrorBoundary.js";
import { groupBy } from "../utils/groupBy.js";
import { AccordionV2 } from "./AccordionV2.js";
import { Badge } from "./Badge.js";
import { Popover } from "./Popover.js";

type ViewUsersVariables = ViewUsersRequest & {
  timezone: string;
};

type ViewUsersProps = ViewUsersVariables & {
  users: ViewUsersResponse;
};

const ViewUsersContext = createContext<ViewUsersProps | null>(null);

const ViewUsersProviderWrapper = ({
  children,
  ...props
}: ViewUsersVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ViewUsersSkeleton />}>
      <ErrorBoundary>
        <ViewUsersProvider {...props}>{children}</ViewUsersProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewUsersProvider = ({
  children,
  ...props
}: ViewUsersVariables & { children: React.ReactNode }) => {
  const { data: users } = useViewUsersSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewUsersContext.Provider value={{ ...props, users }}>
      {children}
    </ViewUsersContext.Provider>
  );
};

const useViewUsers = () => {
  const context = useContext(ViewUsersContext);
  if (!context) {
    throw new Error("useViewUsers must be used within a ViewUsersProvider");
  }
  return context;
};

function hasGroupValue<T extends { _group_value: unknown }>(
  result: T
): result is T & { _group_value: string } {
  return typeof result._group_value === "string";
}

const ViewUsers = () => {
  const { users, id } = useViewUsers();
  // infine scroll not used because there are grouping properties

  // if all users do not have a group value
  if (users.every((user) => !user?._group_value)) {
    return <UserTable users={users} />;
  }
  const usersWithoutNull = users
    .map((result) => ({
      ...result,
      _group_value: result._group_value ?? "undefined"
    }))
    .filter(hasGroupValue);

  const groupedUsers = groupBy(usersWithoutNull, "_group_value");

  return (
    <AccordionV2.PersistentRoot
      persistKey={`view-users-${id}`}
      defaultValue={["undefined"]}
    >
      {Object.entries(groupedUsers).map(([key, users]) => (
        <AccordionV2.Item value={key} key={key}>
          <AccordionV2.Trigger
            className="mobile-pref-view-trigger inline-flex w-full py-0"
            headerClassName=" flex space-x-4 !border-b-0 py-0"
          >
            <h3 className="mobile-pref-section-title dark:border-mono-700/50 flex items-center justify-between gap-4 py-2 text-lg">
              <div className="font-bold">
                <span>{key === "undefined" ? "Users" : key}</span>
              </div>
              <Badge
                count={users.length}
                action="info"
                className="mobile-pref-detail-badge static shrink-0"
              />
            </h3>
          </AccordionV2.Trigger>
          <AccordionV2.Content>
            <UserTable users={users} />
          </AccordionV2.Content>
        </AccordionV2.Item>
      ))}
    </AccordionV2.PersistentRoot>
  );
};

const UserTable = ({ users }: { users: ViewUsersProps["users"] }) => {
  return (
    <div className="md:px-0">
      <div className="mt-8 flow-root">
        <div className="relative w-full">
          <div className="dark:border-mono-700/50 dark:bg-mono-950 max-w-full rounded-md border bg-white shadow">
            <div
              className="grid min-w-full overflow-x-auto"
              style={{
                gridTemplateColumns:
                  "fit-content(150px) repeat(4, minmax(min-content, 1fr))"
              }}
            >
              <div className="contents">
                <div className="mobile-pref-card-title dark:bg-mono-900 text-mono-900 dark:text-mono-100 sticky left-0 z-20 flex items-center bg-white py-1 pl-4 pr-3 text-left text-sm font-semibold shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] md:py-3.5">
                  Name
                </div>
                <div className="mobile-pref-card-title text-mono-900 dark:text-mono-100 dark:bg-mono-900 flex items-center px-3 py-1 text-left text-sm font-semibold md:py-3.5">
                  Role
                </div>
                <div className="mobile-pref-card-title text-mono-900 dark:text-mono-100 dark:bg-mono-900 flex items-center px-3 py-1 text-left text-sm font-semibold md:py-3.5">
                  Aging
                </div>
                <div className="mobile-pref-card-title *:md:py-3.5 text-mono-900 dark:text-mono-100 dark:bg-mono-900 flex items-center px-3 py-1 text-left text-sm font-semibold">
                  Last HQ
                </div>
                <div className="mobile-pref-card-title text-mono-900 dark:text-mono-100 dark:bg-mono-900 flex items-center px-3 py-1 text-left text-sm font-semibold md:py-3.5">
                  Last Workforce
                </div>
              </div>

              {users.map((user, i) => (
                <div
                  key={`${user.user_id ?? "no-user-id"}-${i}`}
                  className="dark:border-mono-700/50 contents border-t"
                >
                  <div className="mobile-pref-card-body dark:bg-mono-950 text-mono-900 dark:text-mono-100 dark:border-mono-700/50 sticky left-0 z-20 whitespace-nowrap border-t bg-white py-4 pl-4 pr-3 text-sm font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    <p className="leading-6">{user.name}</p>
                    {user.email && (
                      <div className="truncate">
                        <a
                          className="mobile-pref-card-meta text-mono-500 block truncate leading-4 underline"
                          href={`mailto:${user.email}`}
                        >
                          {user.email}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="mobile-pref-card-body text-mono-500 dark:border-mono-700/50 border-t px-3 py-4 text-sm leading-4">
                    {user.role}
                  </div>
                  <div className="mobile-pref-card-body text-mono-500 dark:border-mono-700/50 whitespace-nowrap border-t px-3 py-4 text-sm">
                    {user.aging}
                  </div>
                  <div className="mobile-pref-card-body text-mono-500 dark:border-mono-700/50 whitespace-nowrap border-t px-3 py-4 text-sm">
                    <DatePopover date={user.last_interaction_hq} />
                  </div>
                  <div className="mobile-pref-card-body text-mono-500 dark:border-mono-700/50 whitespace-nowrap border-t px-3 py-4 text-sm">
                    <DatePopover date={user.last_interaction_workforce} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DatePopover = ({ date }: { date?: string | null }) => {
  const { timezone } = useViewUsers();
  if (!date) {
    return <p>-</p>;
  }
  const dateInTimezone = DateTime.fromISO(date).setZone(timezone);
  return (
    <Popover.Root>
      <Popover.Trigger className="mobile-pref-inline-button -mx-2 px-2 text-left">
        <p className="mobile-pref-card-body">
          {dateInTimezone.toFormat("MM/dd/yy")}
        </p>
      </Popover.Trigger>
      <Popover.Content align="end" className="text-right">
        <p className="mobile-pref-card-body">
          {dateInTimezone.toFormat("MMMM d, yyyy 'at' h:mm a")}
        </p>
        <p className="mobile-pref-card-meta text-mono-500">
          {dateInTimezone.toRelative()}
        </p>
      </Popover.Content>
    </Popover.Root>
  );
};

const ViewUsersSkeleton = () => {
  return (
    <div className="flex w-full animate-pulse flex-col gap-2">
      <div className="bg-mono-200  dark:bg-mono-900  h-10 rounded" />
      <div className="bg-mono-200  dark:bg-mono-900  h-10 rounded" />
      <div className="bg-mono-200  dark:bg-mono-900    h-16 rounded" />
    </div>
  );
};

ViewUsers.Provider = ViewUsersProviderWrapper;
ViewUsers.useViewUsers = useViewUsers;
export default ViewUsers;
